import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Timer, User, Warning, ArrowRight, Sun, Moon } from "@phosphor-icons/react";

const STAGE_ORDER = ["Preparation", "Cooking", "Chilling", "FreezeDrying", "Packaging", "Completed"];

// IST timezone shift classifier
function shiftOf(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const istHour = (d.getUTCHours() + 5 + Math.floor((d.getUTCMinutes() + 30) / 60)) % 24;
    return istHour >= 6 && istHour < 18 ? "Day" : "Night";
  } catch { return null; }
}

const SHIFT_STYLE = {
  Day:   { stripe: "border-l-4 border-l-amber-400",   badge: "bg-amber-100 text-amber-800",   Icon: Sun },
  Night: { stripe: "border-l-4 border-l-indigo-500",  badge: "bg-indigo-100 text-indigo-800", Icon: Moon },
};

function BatchCard({ b, onAdvance }) {
  const shift = shiftOf(b.created_at);
  const s = shift ? SHIFT_STYLE[shift] : null;
  return (
    <div className={`rounded-md border ${b.delayed ? "border-red-400 bg-red-50" : "border-border bg-card"} ${s?.stripe || ""} p-3 hover:shadow-sm transition-shadow`} data-testid={`kanban-card-${b.batch_no}`}>
      <div className="flex items-center justify-between mb-2 gap-1">
        <div className="mono text-xs font-bold truncate">{b.batch_no}</div>
        <div className="flex items-center gap-1">
          {s && (
            <span className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${s.badge}`} data-testid={`shift-tag-${shift}-${b.batch_no}`}>
              <s.Icon size={9} weight="fill"/>{shift.toUpperCase()}
            </span>
          )}
          {b.delayed && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-red-600"><Warning size={10} weight="fill"/>DELAY</span>}
        </div>
      </div>
      <div className="text-sm font-semibold leading-tight mb-1 truncate">{b.sku_name}</div>
      <div className="text-[11px] text-muted-foreground mono mb-2">{b.sku_code} · {b.planned_qty_kg} kg</div>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-2">
        <span className="inline-flex items-center gap-1"><User size={11}/>{b.created_by || "—"}</span>
        {b.expected_completion && <span className="inline-flex items-center gap-1"><Timer size={11}/>{new Date(b.expected_completion).toLocaleTimeString([], {hour:"2-digit", minute:"2-digit"})}</span>}
      </div>
      <div className="flex items-center justify-between">
        <StatusBadge status={b.qc_status}/>
        {b.stage !== "Completed" && (
          <button onClick={()=>onAdvance(b)} className="text-[11px] font-semibold text-primary hover:underline inline-flex items-center gap-0.5" data-testid={`kanban-advance-${b.batch_no}`}>
            Advance <ArrowRight size={11}/>
          </button>
        )}
      </div>
    </div>
  );
}

export default function Kanban() {
  const [cols, setCols] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/kanban");
      setCols(data);
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const quickAdvance = async (b) => {
    try {
      await api.post(`/batches/${b.id}/advance`, {});
      toast.success(`${b.batch_no} → ${STAGE_ORDER[STAGE_ORDER.indexOf(b.stage)+1] || "Completed"}`);
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Advance failed"));
    }
  };

  const totalBatches = Object.values(cols).reduce((a, v) => a + v.length, 0);

  return (
    <div data-testid="kanban-page">
      <PageHeader subtitle="Live Factory Workflow" title="Batch Kanban Board"
        actions={
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-3 text-[11px] mr-2">
              <span className="inline-flex items-center gap-1"><Sun size={12} weight="fill" className="text-amber-500"/>Day 6–18</span>
              <span className="inline-flex items-center gap-1"><Moon size={12} weight="fill" className="text-indigo-500"/>Night 18–6</span>
            </div>
            <Badge variant="secondary" className="mono">{totalBatches} batches</Badge>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-start">
        {STAGE_ORDER.map(stage => {
          const items = cols[stage] || [];
          return (
            <div key={stage} className="min-w-0" data-testid={`kanban-col-${stage}`}>
              <div className="bg-muted/50 rounded-md p-3 border border-border sticky top-0 z-10">
                <div className="flex items-center justify-between">
                  <div className="overline">{stage}</div>
                  <span className="text-[11px] font-bold mono bg-card text-foreground px-2 py-0.5 rounded-full border border-border">{items.length}</span>
                </div>
              </div>
              <div className="space-y-2 mt-2 min-h-24">
                {items.map(b => <BatchCard key={b.id} b={b} onAdvance={quickAdvance}/>)}
                {items.length === 0 && (
                  <div className="text-[11px] text-muted-foreground text-center py-6 border border-dashed border-border rounded-md">Empty</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
